import { Button, Spinner, Tab, TabList, Text, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { t } from '../../i18n/i18n'
import { Page } from '../Page'
import { Title } from '../../components/common/texts/Title'
import { List } from '../../components/common/list/List'
import { getAnswerOptionColumns } from '../../helpers/lawHelper'
import { useContext } from 'react'
import { DataContext } from '../../App'
import { Add16Regular, ArrowSync16Regular } from '@fluentui/react-icons'
import { NavLink } from 'react-router-dom'
import { Loader } from '../../components/common/spinner/Loader'
import { useState } from 'react'
import { getDemandComplianceModuleColumns } from '../../helpers/demandModuleHelper'

const useStyles = makeStyles({
   alignEnd: {
      alignSelf: 'end',
   },
   justifySelfEnd: {
      justifySelf: 'end',
   },
   paddingL: {
      ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
   },
   maxWidth250: {
      maxWidth: '250px',
   },
   flex: { display: 'flex', flexDirection: 'column', ...shorthands.gap(tokens.spacingVerticalXL) },
   rowReverse: { display: 'flex', flexDirection: 'row-reverse' },
   maintenanceSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
      maxWidth: '480px',
   },
})
export const SystemSettings = () => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { answerOptions, demandComplianceModuleDefinitions, accessToken } = dataContext.state
   const [cacheRefreshing, setCacheRefreshing] = useState(false)

   const [selectedTab, setSelectedTab] = useState('answerOptions')

   const onRefreshCache = async () => {
      setCacheRefreshing(true)
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Cache/invalidate`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
         })
         if (!response.ok) throw new Error()
         dataContext.handleMessage(dataContext.setRootState, 'success', t('RefreshCacheSuccess'), '')
      } catch {
         dataContext.handleMessage(dataContext.setRootState, 'error', t('RefreshCacheFailed'), '')
      } finally {
         setCacheRefreshing(false)
      }
   }

   return (
      <Page
         header={<Title as="h2">{t('SystemSettings')}</Title>}
         subheader={
            <TabList defaultSelectedValue="answerOptions" selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value as string)}>
               <Tab value="answerOptions">{t('AnswerOption')}</Tab>
               <Tab value="demandComplianceModules">{t('DemandComplianceAdditionalModules')}</Tab>
               <Tab value="maintenance">{t('Maintenance')}</Tab>
            </TabList>
         }
      >
         <div className={classes.flex}>
            {selectedTab === 'answerOptions' && (
               <>
                  <div className={classes.rowReverse}>
                     <Button size="small" icon={<Add16Regular />} appearance="transparent">
                        <NavLink style={{ textDecoration: 'none', color: 'black' }} to={`/system-settings/answer-options/new`}>
                           {t('CreateNewAnswerOption')}
                        </NavLink>
                     </Button>
                  </div>
                  {answerOptions === null ? (
                     <Loader />
                  ) : (
                     <List columns={getAnswerOptionColumns()} items={answerOptions.sort((a, b) => a.order - b.order)} title={t('AnswerOption')} asCard />
                  )}
               </>
            )}
            {selectedTab === 'demandComplianceModules' && (
               <>
                  <div className={classes.rowReverse}>
                     <Button size="small" icon={<Add16Regular />} appearance="transparent">
                        <NavLink style={{ textDecoration: 'none', color: 'black' }} to={`/system-settings/demand-compliance-modules/new`}>
                           {t('CreateNew')}
                        </NavLink>
                     </Button>
                  </div>
                  {demandComplianceModuleDefinitions === null ? (
                     <Loader />
                  ) : (
                     <List columns={getDemandComplianceModuleColumns()} items={demandComplianceModuleDefinitions.sort((a, b) => a.order - b.order)} title={t('DemandComplianceModules')} asCard />
                  )}
               </>
            )}
            {selectedTab === 'maintenance' && (
               <div className={classes.maintenanceSection}>
                  <Text weight="semibold">{t('RefreshCache')}</Text>
                  <Text>{t('RefreshCacheDescription')}</Text>
                  <Button
                     appearance="primary"
                     icon={cacheRefreshing ? <Spinner size="tiny" /> : <ArrowSync16Regular />}
                     onClick={onRefreshCache}
                     disabled={cacheRefreshing}
                  >
                     {t('RefreshCache')}
                  </Button>
               </div>
            )}
         </div>
      </Page>
   )
}
